/* ────────────────────────────────────────────────────────────────────────
   sb-hero-shader.js — Shutterbombs reactive hero BACKGROUND engine.

   Mounts the Canvas2D "Burst" particle field (ported from the Claude Design
   heroes.jsx ParticleHero) onto a target element as a pure background layer.
   NO overlay markup — the theme's own hero text / CTA blocks layer on top in
   absolute positioning (see sb-hero-shader.liquid).

   Usage (auto): any element with class `sb-hero-shader` and a child
   `<canvas data-hero-canvas>` is mounted on DOMContentLoaded, reading config
   from data-attributes:
     data-palette='["#e5342f","#1e72e8",...]'   particle/dust hexes
     data-sparks='["#f7f7f5"]'                   bright outrider hexes
     data-bg="#0a0612"                           base fill
     data-light="false"                          true = light bg (no additive glow)

   Usage (manual):  SBHeroShader.mount(canvasEl, { palette, sparks, bg, light })

   Perf: DPR capped at 1.6, RAF paused via IntersectionObserver when offscreen,
   prefers-reduced-motion paints one static frame and skips animation/clicks.
──────────────────────────────────────────────────────────────────────── */
(function (window, document) {
  'use strict';

  function hexToRgb(hex) {
    var h = String(hex).replace('#', '').trim();
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    if (isNaN(n)) return [255, 255, 255];
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function pick(arr) { return arr[(Math.random() * arr.length) | 0]; }

  var DEFAULTS = {
    bg: '#0a0612',
    light: false,
    palette: ['#e5342f', '#f26a21', '#f2c94c', '#2f9e44', '#1e72e8', '#7a42d6', '#ff5da0'],
    sparks: ['#f7f7f5']
  };

  function mount(canvas, opts) {
    if (!canvas || canvas.__sbHeroMounted) return null;
    canvas.__sbHeroMounted = true;

    var cfg = Object.assign({}, DEFAULTS, opts || {});
    var bgRgb = hexToRgb(cfg.bg);
    cfg.veil = 'rgba(' + bgRgb[0] + ',' + bgRgb[1] + ',' + bgRgb[2] + ',' + (cfg.light ? 0.30 : 0.22) + ')';
    var scale = parseFloat(cfg.particleScale);
    if (isNaN(scale) || scale <= 0) scale = 1;
    if (cfg.light && scale <= 1) scale = 1.55;

    var ctx = canvas.getContext('2d');
    if (!ctx) return null;
    var PAL = (cfg.palette && cfg.palette.length ? cfg.palette : DEFAULTS.palette).map(hexToRgb);
    var SPK = (cfg.sparks && cfg.sparks.length ? cfg.sparks : DEFAULTS.sparks).map(hexToRgb);

    var reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var mouse = [-9999, -9999];
    var particles = [];
    var dust = null;
    var dpr = 1;
    var last = performance.now();
    var raf = 0;
    var visible = true;

    function seedDust(w, h) {
      dust = [];
      var count = Math.round((w * h) / (5500 / scale));
      for (var i = 0; i < count; i++) {
        dust.push({
          x: Math.random() * w, y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.25, vy: (Math.random() - 0.5) * 0.25,
          r: (Math.random() * 1.6 + 0.4) * scale,
          rgb: pick(PAL),
          alpha: cfg.light ? (0.55 + Math.random() * 0.4) : (0.25 + Math.random() * 0.6),
          phase: Math.random() * Math.PI * 2
        });
      }
    }

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 1.6);
      var w = Math.max(2, Math.round(canvas.clientWidth * dpr));
      var h = Math.max(2, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w; canvas.height = h;
        seedDust(w, h);
      }
    }

    function detonate(cx, cy) {
      for (var i = 0; i < 90; i++) {
        var a = Math.random() * Math.PI * 2;
        var v = (1 + Math.pow(Math.random(), 0.5) * 9) * dpr;
        particles.push({
          x: cx, y: cy,
          vx: Math.cos(a) * v, vy: Math.sin(a) * v - 0.5 * dpr,
          r: (0.8 + Math.random() * 2.2) * scale, life: 0.6 + Math.random() * 1.1, rgb: pick(PAL)
        });
      }
      for (var j = 0; j < 18; j++) {
        var a2 = Math.random() * Math.PI * 2;
        var v2 = (8 + Math.random() * 8) * dpr;
        particles.push({
          x: cx, y: cy,
          vx: Math.cos(a2) * v2, vy: Math.sin(a2) * v2,
          r: 1.4 * scale, life: 0.8 + Math.random() * 0.6, rgb: pick(SPK)
        });
      }
    }

    function drawFrame(t) {
      var w = canvas.width, h = canvas.height;
      var dt = Math.min((t - last) / 16.67, 3);
      last = t;
      if (!dust) seedDust(w, h);

      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = cfg.veil;
      ctx.fillRect(0, 0, w, h);

      ctx.globalCompositeOperation = cfg.light ? 'source-over' : 'lighter';

      var mx = mouse[0] * dpr, my = mouse[1] * dpr;
      var fr2 = (170 * dpr) * (170 * dpr);

      for (var i = 0; i < dust.length; i++) {
        var d = dust[i];
        var ddx = d.x - mx, ddy = d.y - my, dd = ddx * ddx + ddy * ddy;
        if (dd < fr2) {
          var dist = Math.sqrt(dd) + 0.001;
          var f = (1 - dd / fr2) * 0.7;
          d.vx += (ddx / dist) * f; d.vy += (ddy / dist) * f;
        }
        d.vx *= 0.94; d.vy *= 0.94;
        d.vx += Math.sin(t * 0.0003 + d.phase) * 0.01;
        d.vy += Math.cos(t * 0.0004 + d.phase * 1.3) * 0.01;
        d.x += d.vx * dt; d.y += d.vy * dt;
        if (d.x < -10) d.x += w + 20; if (d.x > w + 10) d.x -= w + 20;
        if (d.y < -10) d.y += h + 20; if (d.y > h + 10) d.y -= h + 20;
        ctx.fillStyle = 'rgba(' + d.rgb[0] + ',' + d.rgb[1] + ',' + d.rgb[2] + ',' + d.alpha + ')';
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r * dpr, 0, Math.PI * 2);
        ctx.fill();
      }

      for (var k = particles.length - 1; k >= 0; k--) {
        var p = particles[k];
        p.life -= dt * 0.014;
        if (p.life <= 0) { particles.splice(k, 1); continue; }
        p.vy += 0.04 * dt; p.vx *= 0.985; p.vy *= 0.985;
        p.x += p.vx * dt; p.y += p.vy * dt;
        var aa = Math.max(0, p.life);
        var size = p.r * dpr * (0.5 + aa);
        var R = p.rgb[0], G = p.rgb[1], B = p.rgb[2];
        var grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, size * 3);
        grd.addColorStop(0,   'rgba(' + R + ',' + G + ',' + B + ',' + (aa * 0.9) + ')');
        grd.addColorStop(0.4, 'rgba(' + R + ',' + G + ',' + B + ',' + (aa * 0.35) + ')');
        grd.addColorStop(1,   'rgba(' + R + ',' + G + ',' + B + ',0)');
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(p.x, p.y, size * 3, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalCompositeOperation = 'source-over';
      var vg = ctx.createRadialGradient(w / 2, h / 2, h * 0.4, w / 2, h / 2, h * 0.95);
      vg.addColorStop(0, 'rgba(0,0,0,0)');
      vg.addColorStop(1, cfg.light ? 'rgba(180,140,90,0.18)' : 'rgba(0,0,0,0.55)');
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, w, h);
    }

    function loop(t) {
      if (!visible) { raf = requestAnimationFrame(loop); return; }
      resize();
      drawFrame(t);
      raf = requestAnimationFrame(loop);
    }

    // pointer-events live on the canvas wrapper; CTAs that opt back in won't
    // bubble a detonate because they sit in a sibling layer above this one.
    var hitTarget = canvas.parentElement || canvas;
    hitTarget.addEventListener('mousemove', function (e) {
      var r = canvas.getBoundingClientRect();
      mouse = [e.clientX - r.left, e.clientY - r.top];
    });
    hitTarget.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-cta], a, button')) return;
      var r = canvas.getBoundingClientRect();
      detonate((e.clientX - r.left) * dpr, (e.clientY - r.top) * dpr);
    });
    window.addEventListener('resize', resize);

    // Pause when offscreen.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (es) {
        visible = es[0].isIntersecting;
      }, { threshold: 0.01 });
      io.observe(canvas);
    }

    ctx.fillStyle = cfg.bg;
    resize();
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (reduceMotion) {
      drawFrame(performance.now()); // single static paint
    } else {
      raf = requestAnimationFrame(loop);
    }

    return { detonate: detonate, destroy: function () { cancelAnimationFrame(raf); } };
  }

  function parseJSONAttr(el, name) {
    var raw = el.getAttribute(name);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  function autoMount(root) {
    (root || document).querySelectorAll('.sb-hero-shader').forEach(function (wrap) {
      var canvas = wrap.querySelector('canvas[data-hero-canvas]');
      if (!canvas) return;
      mount(canvas, {
        palette: parseJSONAttr(wrap, 'data-palette'),
        sparks: parseJSONAttr(wrap, 'data-sparks'),
        bg: wrap.getAttribute('data-bg') || undefined,
        light: wrap.getAttribute('data-light') === 'true',
        particleScale: parseFloat(wrap.getAttribute('data-particle-scale') || '1')
      });
    });
  }

  window.SBHeroShader = { mount: mount, autoMount: autoMount };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { autoMount(); });
  } else {
    autoMount();
  }
  // Re-mount in the Shopify theme editor when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) { autoMount(e.target); });
})(window, document);
